import TuneIcon from "@mui/icons-material/Tune";
import { Box, Slider, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { AnimatedNumber } from "../../../../components/UIToolkit/Animations/AnimatedNumber";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { SettingRow } from "../../../../components/UIToolkit/molecules/SettingRow";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { CollapsibleSection } from "../../../../components/UIToolkit/Panels/CollapsibleSection";
import type { RecurringCalculationSettings, RequestedPattern } from "../../../../services/backend.service";
import { FONT_SIZE, INK, STATUS_BADGE } from "../../../../theme/tokens";
import { DEFAULT_CUSTOM_CYCLE_WEEKS, REQUESTED_PATTERN_LABELS, REQUESTED_PATTERN_ORDER, scoreGuidance } from "./recurringDisplay";

type Props = {
	settings: RecurringCalculationSettings;
	onChange: (next: RecurringCalculationSettings) => void;
	/**
	 * Jobs the department actually uses, learned from a previous run's derived hours. Empty before
	 * the first run — which is why the per-job override is hidden until then rather than shown blank.
	 */
	jobNames: string[];
	disabled?: boolean;
};

const SCORE_STEP = 0.05;
const SCORE_MARKS = [0, 0.25, 0.5, 0.75, 1].map((value) => ({ value, label: value.toFixed(2) }));

/** The score is the one number that decides how much wrong data lands on a real schedule — so it leads. */
const SCORE_READOUT_SX = {
	fontSize: FONT_SIZE.TITLE_LG,
	fontWeight: 700,
	lineHeight: 1,
	color: STATUS_BADGE.BRAND_FALLBACK,
	fontVariantNumeric: "tabular-nums",
} as const;

const WINDOW_FIELD_SX = { width: 120 } as const;

const isCycleWeeks = (value: string): boolean => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 26;

const isPositiveInteger = (value: string): boolean => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 24;

/**
 * Everything the operator decides before a run (design §3b).
 *
 * Deliberately NOT asked here: which source to trust (schedule vs punches). The fit decides that per
 * employee and reports it on the row — it can answer better than a person can and then show its
 * working, so making it a setting would only invite a worse answer.
 */
export const RecurringSettingsPanel: FC<Props> = ({ settings, onChange, jobNames, disabled = false }) => {
	const [perJobOpen, setPerJobOpen] = useState(false);

	const patch = (fields: Partial<RecurringCalculationSettings>) => onChange({ ...settings, ...fields });

	const setJobPattern = (jobName: string, pattern: RequestedPattern) => {
		const next = { ...settings.patternByJob };
		// "Same as above" is the absence of a key, not a value — the backend falls back to `pattern`.
		if (pattern === settings.pattern) delete next[jobName];
		else next[jobName] = pattern;
		patch({ patternByJob: next });
	};

	return (
		<SettingsSection
			title="Calculation settings"
			icon={<TuneIcon />}
			description="These decide what gets proposed. Nothing is written until you approve the results."
			layout="stack"
			contentMaxWidth="none"
			dataTestId="recurring-settings-section"
		>
			<SettingRow
				label="Minimum score"
				description="A proposal below this lands in Needs review instead of Ready. The whole 0.00–1.00 range is yours."
				stacked
				dataTestId="recurring-minimum-score-row"
				control={
					<Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%" }}>
						<Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
							<Slider
								value={settings.minimumScore}
								min={0}
								max={1}
								step={SCORE_STEP}
								marks={SCORE_MARKS}
								disabled={disabled}
								onChange={(_, value) => patch({ minimumScore: Array.isArray(value) ? value[0] : value })}
								aria-label="Minimum score"
								data-testid="recurring-minimum-score-slider"
							/>
						</Box>
						<Typography component="output" sx={SCORE_READOUT_SX} data-testid="recurring-minimum-score-value">
							<AnimatedNumber value={Math.round(settings.minimumScore * 100)} />%
						</Typography>
					</Box>
				}
			/>
			<InlineHint tone={settings.minimumScore < 0.6 ? "warning" : "info"} dataTestId="recurring-score-guidance">
				{scoreGuidance(settings.minimumScore)}
			</InlineHint>

			<SettingRow
				label="Pattern"
				description="What to look for. Auto tries every shape and keeps the simplest one that fits."
				fullWidthControl
				dataTestId="recurring-pattern-row"
				control={
					<SystemDropdown<RequestedPattern>
						label="Pattern"
						fullWidth
						disabled={disabled}
						data-testid="recurring-pattern-select"
						selectedItem={settings.pattern}
						onChange={(pattern) => {
							if (!pattern) return;
							// "Other" with no length is a request the backend cannot honour, so the default goes
							// in with the selection rather than waiting for the operator to notice.
							patch(pattern === "custom" ? { pattern, customCycleWeeks: settings.customCycleWeeks ?? DEFAULT_CUSTOM_CYCLE_WEEKS } : { pattern });
						}}
						items={REQUESTED_PATTERN_ORDER.map((value) => ({ id: value, value, label: REQUESTED_PATTERN_LABELS[value] }))}
					/>
				}
			/>
			{settings.pattern === "custom" && (
				<>
					<SettingRow
						label="Cycle length (weeks)"
						description="The pattern repeats every this many weeks, on the weekdays the fit finds inside that cycle."
						dataTestId="recurring-custom-cycle-row"
						control={
							<SystemValidationTextField
								label="Weeks"
								inputType="number"
								disabled={disabled}
								value={String(settings.customCycleWeeks ?? DEFAULT_CUSTOM_CYCLE_WEEKS)}
								formControlSx={WINDOW_FIELD_SX}
								dataTestId="recurring-custom-cycle-field"
								validation={{
									validationCallback: isCycleWeeks,
									helperText: { invalid: "1\u201326" },
									performValidation: "always",
									showValidWhenValid: "never",
								}}
								onChange={(event) => {
									const weeks = Number(event.target.value);
									if (Number.isInteger(weeks) && weeks >= 1 && weeks <= 26) patch({ customCycleWeeks: weeks });
								}}
							/>
						}
					/>
					<InlineHint tone="info" dataTestId="recurring-manual-pattern-hint">
						Auto never proposes a long cycle on its own — one has enough freedom to fit almost any history, so the search leaves it alone. Naming the length
						here is what makes it available, so use it when you know the roster really runs on that cycle.
					</InlineHint>
				</>
			)}

			{jobNames.length > 0 && (
				<CollapsibleSection
					title={`Different pattern per job (${Object.keys(settings.patternByJob).length} overridden)`}
					open={perJobOpen}
					onToggle={() => setPerJobOpen((open) => !open)}
					dataTestId="recurring-per-job-pattern-toggle"
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
							A job left on "Same as above" uses the pattern you chose for the department.
						</Typography>
						{jobNames.map((jobName) => (
							<SettingRow
								key={jobName}
								label={jobName}
								fullWidthControl
								dataTestId={`recurring-job-pattern-row-${jobName}`}
								control={
									<SystemDropdown<RequestedPattern>
										fullWidth
										disabled={disabled}
										data-testid={`recurring-job-pattern-select-${jobName}`}
										selectedItem={settings.patternByJob[jobName] ?? settings.pattern}
										onChange={(pattern) => {
											if (pattern) setJobPattern(jobName, pattern);
										}}
										items={REQUESTED_PATTERN_ORDER.map((value) => ({
											id: value,
											value,
											label: value === settings.pattern ? `Same as above — ${REQUESTED_PATTERN_LABELS[value]}` : REQUESTED_PATTERN_LABELS[value],
										}))}
									/>
								}
							/>
						))}
					</Box>
				</CollapsibleSection>
			)}

			<SettingRow
				label="History window (months)"
				description="How far back to read. The run reports what it scores on exactly this window — it never quietly answers from a longer one."
				dataTestId="recurring-window-row"
				control={
					<SystemValidationTextField
						label="Months"
						inputType="number"
						disabled={disabled}
						value={String(settings.baseWindowMonths)}
						formControlSx={WINDOW_FIELD_SX}
						dataTestId="recurring-base-window-field"
						validation={{
							validationCallback: isPositiveInteger,
							helperText: { invalid: "1\u201324" },
							performValidation: "always",
							showValidWhenValid: "never",
						}}
						onChange={(event) => {
							const months = Number(event.target.value);
							if (Number.isInteger(months) && months >= 1 && months <= 24) patch({ baseWindowMonths: months });
						}}
					/>
				}
			/>
		</SettingsSection>
	);
};
